/**
 * One Refresh press → re-read the tracker for everything the drawer shows.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Deliberately does NOT touch the config query. The drawer copies
 * `configQuery.data` straight into its editable draft
 * (FlowOptimizerDrawer.tsx), so refetching it would replace every unsaved rule,
 * formula and capacity edit with no prompt — and disarm the unsaved-changes
 * guard in the same tick. The config is a local-DB row the drawer is already
 * editing; it carries no weights and no liveness, so there is nothing to gain.
 *
 * The flow re-read is skipped while the Standard tab has unsaved edits, for the
 * same reason (its draft is seeded from the query).
 *
 * Legs run together and failures are collected, not thrown: one dead leg must
 * not hide the three that worked, and stale data on screen beats a blank panel.
 */

import { useCallback, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import { queueProviderOf } from '@/features/trackers/flow-optimizer/queue-provider';

import { queueKeys } from '../hooks/useFlowOptimizerQueue';
import { useFlowOptimizerDrawerFooter } from '../FlowOptimizerDrawerFooterContext';
import type { FlowOptimizerProvider } from '../services/flow-optimizer-api';

/** A second press inside this window would re-read caches that have not moved. */
const COOLDOWN_MS = 15_000;

/** The queue legs — only for a tracker with a queue (none on Tres Commas). */
function queueLegs(
  qc: QueryClient,
  provider: FlowOptimizerProvider,
  campaignId: string,
): Array<Promise<unknown>> {
  const queue = queueProviderOf(provider);
  if (!queue) return [];
  return [
    // The only source of flowActive/flowWeight and the live set. refetch, not
    // invalidate: "is it still live" deferred to the next tab mount would
    // make the button's own tooltip a lie.
    qc.refetchQueries({ queryKey: queueKeys.queue(queue, campaignId) }),
    // Local-DB reads nobody may be looking at — marking them stale is enough.
    qc.invalidateQueries({
      queryKey: queueKeys.rotationHistory(queue, campaignId),
    }),
    qc.invalidateQueries({
      queryKey: [queue, 'flow-optimizer', 'queue-events', campaignId],
    }),
  ];
}

export function useDrawerRefresh(input: {
  campaignId: string;
  provider: FlowOptimizerProvider;
}) {
  const { campaignId, provider } = input;
  const qc = useQueryClient();
  const toast = useToast();
  const { refreshSlot } = useFlowOptimizerDrawerFooter();
  const [refreshing, setRefreshing] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(0);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    const legs = queueLegs(qc, provider, campaignId);
    if (refreshSlot?.refetchWeights) {
      legs.push(Promise.resolve(refreshSlot.refetchWeights()));
    }
    if (refreshSlot?.refetchFlow && !refreshSlot.flowDirty) {
      legs.push(Promise.resolve(refreshSlot.refetchFlow()));
    }
    const settled = await Promise.allSettled(legs);
    setRefreshing(false);
    setLastRunAt(Date.now());
    const failed = settled.find((s) => s.status === 'rejected');
    if (failed && failed.status === 'rejected') {
      toast.error('Refresh failed', getApiErrorMessage(failed.reason));
    }
  }, [qc, provider, campaignId, refreshSlot, toast]);

  return {
    refresh,
    refreshing,
    /** True right after a run — the tracker's own caches have not moved yet. */
    cooling: lastRunAt > 0 && Date.now() - lastRunAt < COOLDOWN_MS,
    /** Whether the weights leg will actually run (CUSTOM mode only). */
    refreshesWeights: Boolean(refreshSlot?.refetchWeights),
    /** Whether the Standard flow leg is being held back by unsaved edits. */
    flowHeldByDirty: Boolean(refreshSlot?.refetchFlow && refreshSlot.flowDirty),
    refreshesFlow: Boolean(refreshSlot?.refetchFlow && !refreshSlot.flowDirty),
  };
}
